import moment, { Moment } from "moment";

export type DateType = Moment | Date | string | number;

export type DateFromUntil = {
  from: string;
  until: string;
};

export const isSameDay = (dayA: DateType, dayB: DateType): boolean => {
  const first = moment(dayA).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  const second = moment(dayB).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  return first.isSame(second);
};

export const isBirthmonth = (birthday: DateType): boolean => {
  return (
    convertDateToMoment(birthday).month() === convertDateToMoment().month()
  );
};

export const isBeforeToday = (today: DateType, dayB: DateType): boolean => {
  const first = moment(today).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  const second = moment(dayB);

  return second.isBefore(first);
};

export const isInMonth = (compared: DateType, compare: DateType): boolean => {
  const first = moment(compared);
  const second = moment(compare);

  return first.year() === second.year() && first.month() === second.month();
};

export const convertDateToZone = (date: DateType): string =>
  `${moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS")}Z`;

export const convertDateToZeroZone = (date: DateType): string =>
  `${moment(date).utc().format("YYYY-MM-DDTHH:mm:ss.SSS")}Z`;

export const convertDateToMoment = (date?: DateType): Moment =>
  date ? moment(date) : moment(new Date());

export const formatDate = (date: DateType, format: string): string => {
  return convertDateToMoment(date).format(format);
};

export const getDay = (date: DateType): number =>
  convertDateToMoment(date).day();

export const getDateHour = (date: DateType): number => {
  if (!moment(date).isValid())
    throw new Error("This is not the correct Date Format.");
  return moment(date).hour();
};

export const getPmOrAm = (date: DateType) => {
  const dateTime = convertDateToMoment(date);
  const hour = dateTime.hour();
  const amPm = hour <= 11 ? "上午" : hour === 12 ? "中午" : "下午";

  return dateTime.format(`${amPm}h:mm`);
};

export const getDayFromStartUntilEnd = (date: DateType): DateFromUntil => {
  return {
    from: convertDateToZeroZone(getDayStart(date)),
    until: convertDateToZeroZone(getDayEnd(date)),
  };
};

export const getDayStart = (date: DateType): Moment => {
  return moment(date).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
};

export const getDayEnd = (date: DateType): Moment => {
  return moment(date).set({
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 99,
  });
};

export const getFormalTWDateTime = (date: DateType): string => {
  return `${formatDate(date, "YYYY/MM/DD")} ${getPmOrAm(date)}`;
};

export function getDateTimeRangePayload(
  startAt: Moment,
  endAt: Moment
): {
  from: string;
  until: string;
} {
  return {
    from: startAt.clone().startOf("day").utcOffset(0).format(),
    // The until time is not included. In order to fetch the whole day report,
    // we have to set the endAt to tomorrow. This behavior has been confirmed with
    // backend.
    until: endAt.clone().endOf("day").utcOffset(0).format(),
  };
}
